import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParallaxHero from '../components/ParallaxHero';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', organization: '', role: 'corporate', intent: 'demo', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [heroVisible, setHeroVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -30px 0px' };
    
    const createObserver = (setVisible) => new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, observerOptions);

    const heroObserver = createObserver(setHeroVisible);
    const contentObserver = createObserver(setContentVisible);

    // Trigger hero animation on mount
    setTimeout(() => setHeroVisible(true), 100);
    
    if (contentRef.current) contentObserver.observe(contentRef.current);

    return () => {
      heroObserver.disconnect();
      contentObserver.disconnect();
    };
  }, []);

  // Warm Premium Palette - Consistent with Homepage
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4A7D78',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    bgWarmWhite: '#FFFDFB',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
    borderSage: '#C5D5CF',
    success: '#4D9078',
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      setSubmitStatus(response.ok ? 'success' : 'error');
      if (response.ok) setFormData({ name: '', email: '', organization: '', role: 'corporate', intent: 'demo', message: '' });
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'contact@csrexchange.in', href: 'mailto:contact@csrexchange.in' },
    { icon: Phone, label: 'Phone', value: '+91 22 1234 5678', href: 'tel:+912212345678' },
    { icon: MapPin, label: 'Office', value: 'Mumbai, Maharashtra, India', href: null }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />

      {/* Hero with Parallax */}
      <ParallaxHero 
        backgroundColor={colors.primaryTeal}
        intensity={0.25}
        className="pt-32 pb-16 md:pt-40 md:pb-20 px-6"
      >
        <div className="max-w-6xl mx-auto text-center">
          <div 
            className={`w-16 h-16 rounded-xl mx-auto mb-6 flex items-center justify-center transition-all duration-700 ${heroVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }}
          >
            <Mail className="w-8 h-8" style={{ color: colors.textOnDark }} />
          </div>
          <h1 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ color: colors.textOnDark, transitionDelay: heroVisible ? '100ms' : '0ms' }}
          >
            Let&apos;s Talk Impact
          </h1>
          <p 
            className={`text-lg md:text-xl max-w-2xl mx-auto transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ color: colors.textOnDarkMuted, transitionDelay: heroVisible ? '200ms' : '0ms' }}
          >
            Whether you&apos;re looking to fund projects, scale your NGO, or partner with us — we&apos;re here to help.
          </p>
        </div>
      </ParallaxHero>

      {/* Contact Form Section */}
      <section ref={contentRef} className="py-16 md:py-24 px-6" style={{ backgroundColor: colors.bgCream }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 
                className={`text-2xl md:text-3xl font-bold mb-6 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ color: colors.textPrimary }}
              >
                Get in Touch
              </h2>
              <p 
                className={`mb-10 leading-relaxed transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ color: colors.textSecondary, transitionDelay: contentVisible ? '100ms' : '0ms' }}
              >
                Our team is ready to assist you with any questions about CSR compliance, NGO partnerships, or platform features.
              </p>
              
              <div className="space-y-6">
                {contactInfo.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-start gap-4 transition-all duration-500 group ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: contentVisible ? `${200 + idx * 100}ms` : '0ms' }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-6"
                      style={{ backgroundColor: colors.bgSage }}
                    >
                      <item.icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" style={{ color: colors.primaryTeal }} />
                    </div>
                    <div>
                      <p 
                        className="text-sm font-medium mb-1"
                        style={{ color: colors.textTertiary }}
                      >
                        {item.label}
                      </p>
                      {item.href ? (
                        <a 
                          href={item.href}
                          className="font-semibold transition-colors duration-200"
                          style={{ color: colors.textPrimary }}
                          onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryTeal}
                          onMouseLeave={(e) => e.currentTarget.style.color = colors.textPrimary}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-semibold" style={{ color: colors.textPrimary }}>{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div 
                className={`rounded-2xl p-8 md:p-10 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bgCard, 
                  border: `1px solid ${colors.borderWarm}`,
                  transitionDelay: contentVisible ? '300ms' : '0ms'
                }}
              >
                {submitStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div 
                      className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                      style={{ backgroundColor: colors.bgSage }}
                    >
                      <CheckCircle className="w-8 h-8" style={{ color: colors.success }} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2" style={{ color: colors.textPrimary }}>Message Sent!</h3>
                    <p style={{ color: colors.textSecondary }}>We&apos;ll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl transition-all duration-200 outline-none"
                          style={{ 
                            backgroundColor: colors.bgCream,
                            border: `1.5px solid ${colors.borderWarm}`,
                            color: colors.textPrimary
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.primaryTeal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px rgba(61, 107, 103, 0.12)`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.borderWarm;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="John Doe"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl transition-all duration-200 outline-none"
                          style={{ 
                            backgroundColor: colors.bgCream,
                            border: `1.5px solid ${colors.borderWarm}`,
                            color: colors.textPrimary
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.primaryTeal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px rgba(61, 107, 103, 0.12)`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.borderWarm;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="john@company.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>Organization</label>
                        <input
                          type="text"
                          name="organization"
                          value={formData.organization}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl transition-all duration-200 outline-none"
                          style={{ 
                            backgroundColor: colors.bgCream,
                            border: `1.5px solid ${colors.borderWarm}`,
                            color: colors.textPrimary
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.primaryTeal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px rgba(61, 107, 103, 0.12)`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.borderWarm;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          placeholder="Company Name"
                          data-testid="contact-organization"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>I am a</label>
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl transition-all duration-200 outline-none"
                          style={{ 
                            backgroundColor: colors.bgCream,
                            border: `1.5px solid ${colors.borderWarm}`,
                            color: colors.textPrimary
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.primaryTeal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px rgba(61, 107, 103, 0.12)`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.borderWarm;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          data-testid="contact-role"
                        >
                          <option value="corporate">Corporate / CSR Team</option>
                          <option value="ngo">NGO / Implementing Agency</option>
                          <option value="consultant">Consultant / Advisor</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200 outline-none resize-none"
                        style={{ 
                          backgroundColor: colors.bgCream,
                          border: `1.5px solid ${colors.borderWarm}`,
                          color: colors.textPrimary
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = colors.primaryTeal;
                          e.currentTarget.style.boxShadow = `0 0 0 3px rgba(61, 107, 103, 0.12)`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = colors.borderWarm;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        placeholder="How can we help you?"
                        data-testid="contact-message"
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div 
                        className="flex items-center gap-2 p-4 rounded-xl"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
                      >
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="text-sm text-red-600">Something went wrong. Please try again.</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 hover:-translate-y-0.5"
                      style={{ 
                        backgroundColor: colors.primaryTeal,
                        color: colors.textOnDark,
                        boxShadow: '0 4px 14px rgba(61, 107, 103, 0.3)'
                      }}
                      onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = colors.primaryTealLight; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primaryTeal; }}
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
